<template>
  <v-card>
    <v-card-title>
      <p class="pl-6">{{ $tc("skills.title", 2) }}</p>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        :label="$t('common.search')"
        single-line
        hide-details
      >
      </v-text-field>
    </v-card-title>
    <p class="pa-5">{{ $t("skills.description_grid") }}</p>
    <v-spacer></v-spacer>
    <v-data-table
      :headers="headers"
      :items="list"
      :loading="loading"
      item-key="number"
      group-by="category.id"
      class="elevation-1"
      :footer-props="footer"
      :loading-text="$t('common.loading')"
      :no-results-text="$t('common.no_results')"
      :server-items-length="$store.state.skill.totalCount"
      :options.sync="options"
      dense
    >
      <template v-slot:group.header="{ items }">
        <th colspan="4">
          {{ items[0].category.name | capitalize }}
        </th>
      </template>
      <template v-slot:top>
        <v-toolbar flat>
          <v-spacer></v-spacer>
          <v-dialog v-model="dialog" max-width="500px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" dark class="mb-2" v-bind="attrs" v-on="on">
                {{ $t("common.add") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{ $t(formTitle) }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-form ref="form" v-model="valid">
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          rows="2"
                          v-model="editedItem.name"
                          :label="$t('common.name')"
                          required
                          :rules="required_rule"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          rows="2"
                          v-model="editedItem.classification"
                          :label="$t('skills.classification_label')"
                          required
                        ></v-textarea>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          rows="2"
                          v-model="editedItem.goal_one"
                          :label="$t('skills.goal_one')"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          rows="2"
                          v-model="editedItem.goal_two"
                          :label="$t('skills.goal_two')"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-textarea
                          rows="2"
                          v-model="editedItem.goal_three"
                          :label="$t('skills.goal_three')"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <SkillCategoryPicker
                          required
                          v-model="editedItem.category"
                          :label="$tc('skills.categories.title', 1)"
                        />
                      </v-col>
                    </v-row>
                  </v-form>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="close">
                  {{ $t("common.cancel") }}
                </v-btn>
                <v-btn
                  color="blue darken-1"
                  text
                  @click="_submit()"
                  :loading="loading"
                >
                  {{ $t("common.save") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:no-data>
        {{ $t("common.no_results") }}
      </template>
      <template v-slot:item.name="{ item }">
        {{ item.name }}
      </template>
      <template #item.skill="{ item }">
        <v-checkbox
          :value="item.id"
          multiple
          @change="updateSkills(item)"
          v-model="skills"
          dense
        />
      </template>
      <template #item.intervention="{ item }">
        <v-checkbox
          :value="item.id"
          multiple
          @change="updateInterventions(item)"
          v-model="interventions"
          dense
        />
      </template>
      <template v-slot:item.actions="{ item }">
        <EditDelete
          @edit="editItem(item)"
          @delete="remove('skill', Object.assign({ id: item.number }, item))"
        />
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { ADD, DELETE, FETCH, UPDATE } from "@/store/action-types"
import TableCrudMixin from "@/mixins/TableCrudMixin"
import { mapActions } from "vuex"
import TableMixin from "@/mixins/TableMixin"
import EditDelete from "@/components/form/EditDelete"
import SkillCategoryPicker from "@/components/form/SkillCategoryPicker"

export default {
  name: "SkillsTable",
  components: { SkillCategoryPicker, EditDelete },
  mixins: [TableCrudMixin, TableMixin],
  data: () => ({
    skills: [],
    interventions: [],
    editedItem: {
      name: null,
      classification: null,
      goal_one: "",
      goal_two: "",
      goal_three: "",
      category: {},
    },
    valid: true,
  }),
  mounted() {},
  computed: {
    required_rule() {
      return [(v) => !!v || this.$t("errors.field_required")]
    },
    list() {
      return this.$store.state.skill.data
    },

    headers() {
      return [
        {
          value: "name",
          text: this.$tc("common.name", 1),
          sortable: false,
        },
        {
          value: "skill",
          text: this.$tc("skills.title", 1),
          sortable: false,
        },
        {
          value: "intervention",
          text: this.$tc("intervention.title", 1),
          sortable: false,
        },
        {
          value: "actions",
          align: "right",
        },
      ]
    },
  },
  watch: {
    list(val) {
      if (val !== undefined) {
        this.skills = val.filter((e) => e.is_checked).map((e) => e.id)
      }
      if (val !== undefined) {
        this.interventions = val
          .filter((e) => e.is_intervention_checked)
          .map((e) => e.id)
      }
    },
  },
  methods: {
    updateInterventions(e) {
      this.updateIntervention({ skill_id: e.id })
    },
    updateSkills(e) {
      this.updateSkill({ skill_id: e.id })
    },

    ...mapActions("skill_client", { updateSkill: ADD }),
    ...mapActions("intervention_type", { updateIntervention: ADD }),
    ...mapActions("skill", [FETCH, ADD, DELETE, UPDATE]),

    _submit() {
      if (!this.$refs.form.validate()) {
        this.$toast.warning(this.$t("errors.fillMissingFields"))
        return
      }
      this.submit(
        [
          "name",
          "id",
          "goal_one",
          "goal_two",
          "goal_three",
          "classification",
          "category",
        ],
        "category"
      )
    },
  },
}
</script>
